import { useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

export interface ToconlineSyncRun {
  id: string;
  started_at: string;
  finished_at: string | null;
  trigger_source: string;
  status: string;
  steps: Array<{ key: string; ok: boolean; ms?: number; error?: string; data?: unknown }>;
  error: string | null;
}

const QUERY_KEYS_TO_REFRESH = [
  // CRM
  "client-invoices",
  "client-invoices-company",
  "crm-client-360",
  "crm-clients-360",
  "crm-clients-enriched",
  "crm-clients-enriched-v2",
  "crm-clients-extras",
  "crm-clients-kpis",
  "crm-clients-kpis-v2",
  "crm-clients",
  "commercial-dashboard",
  // Dívida / recebimentos
  "client-outstanding-debt",
  "rpt-client-outstanding-debt",
  "company-debts",
  "bank-dossier-debt",
  "receivables",
  "receivables-kpi-docs",
  "recon-entity-receipts",
  "pending-invoices-check",
  // Vendas
  "toconline-sales",
  "toconline-sales-history",
  "toconline-sales-history-all",
  "client-sales-history",
  "sales-kpi-toconline",
  "sales-pending-all-toconline",
  // Compras
  "toconline-purchases-history",
  "toconline-purchases-history-all",
  "client-purchases-history",
  "accounting-purchases",
  "purchases-paid-expenses",
  // Contabilidade / dashboards
  "consolidated-metrics",
  "accounting-dashboard",
  "accounting-orders",
  "accounting-expenses",
  "account-dashboard",
];


/**
 * Reads the latest TOConline sync run (admins only) and invalidates the
 * billing / CRM caches whenever a new run finishes, so the client area and
 * the CRM always show freshly synced data without a manual reload.
 */
export function useToconlineSyncSignal(pollMs = 60_000) {
  const { isAdmin, user } = useAuth();
  const qc = useQueryClient();
  const lastSeen = useRef<string | null>(null);

  const query = useQuery({
    queryKey: ["toconline-sync-runs", "latest"],
    enabled: !!user && !!isAdmin,
    refetchInterval: pollMs,
    staleTime: pollMs / 2,
    queryFn: async (): Promise<ToconlineSyncRun | null> => {
      const { data, error } = await supabase
        .from("toconline_sync_runs" as any)
        .select("id, started_at, finished_at, trigger_source, status, steps, error")
        .order("started_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as ToconlineSyncRun) ?? null;
    },
  });

  const finishedAt = query.data?.finished_at ?? null;

  useEffect(() => {
    if (!finishedAt) return;
    if (lastSeen.current === null) {
      lastSeen.current = finishedAt;
      return;
    }
    if (lastSeen.current !== finishedAt) {
      lastSeen.current = finishedAt;
      QUERY_KEYS_TO_REFRESH.forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    }
  }, [finishedAt, qc]);

  return query;
}
